// Copyright (c) Docugami, Inc. All rights reserved.

/* Chat api models */

export interface ChatOptions {
    docsetId: string;
}

interface RawChatOptions {
    docsetId: string;
}

export const parseChatOptionsResponse = (options: RawChatOptions) => ({ ...options });

export interface RawTextChatMessage {
    messageId: string;
    timestamp: string;
    text: string;
    isFinal: boolean;
}

export interface RawSystemTextChatMessage extends RawTextChatMessage {
    parentId: string;
    citations: Citation[];
    feedback: FeedbackModel | null;
}

export interface TextChatMessage {
    id: string;
    timestamp: number;
    text: string;
    isFinal: boolean;
    sessionId: string | undefined;
}

interface BaseSystemTextChatMessage extends TextChatMessage {
    parentId: string;
}

export interface SystemTextChatMessage extends BaseSystemTextChatMessage {
    citations: Citation[];
    feedback: FeedbackModel | null;
}

export interface SystemErrorMessage extends BaseSystemTextChatMessage {
    error: string;
}

export type SystemChatMessage = SystemTextChatMessage | SystemErrorMessage;

export type Citation = CitationModel | CitationModelV2;

export enum CitationType {
    Document = "Document",
    Report = "Report"
}

export interface CitationModel {
    label: string;
    citationType: CitationType;
    reportQuery: string;
    documentId: string;
}

export interface CitationModelV2 {
    documentId: string;
    spanId: string;
    richText?: string;
}

export const isCitationV2 = (citation: Citation): citation is CitationModelV2 => !!(citation as CitationModelV2).spanId;

export interface FeedbackModel {
    id: string;
    feedback: boolean;
    messageId: string;
    comment?: string;
}

export const isSystemMessage = (message: TextChatMessage): message is SystemChatMessage => !!(message as SystemChatMessage).parentId;
export const isSystemError = (message: TextChatMessage): message is SystemErrorMessage => isSystemMessage(message) && !!(message as SystemErrorMessage).error;

/* Request/response models */

export interface DocSetChatResponse {
    options: RawChatOptions;
    suggestions: string[];
    chatHistory?: RawTextChatMessage[];
}

export type ChatMessagePair = {
    humanMessage: string,
    aiMessage: string
}

export interface SendMessageRequest {
    message: RawTextChatMessage;
    options: ChatOptions;
    chatHistory: ChatMessagePair[];
}

export interface SendMessageResponse {
    message: RawSystemTextChatMessage;
}

export interface StreamingChatMessageRequest {
    options: ChatOptions;
    message: string;
    chatHistory: ChatMessagePair[];
    sessionId?: string;
}

export interface SendStreamingChatMessageResponse<MessageType> {
    message: MessageType;
    sessionId?: string;
}

export type StreamedChatMessageResponse = StreamedChatResponsePayload | StreamedChatResponseError;

interface StreamedChatResponsePayload {
    options: RawChatOptions;
    message: RawSystemTextChatMessage;
    sessionId?: string;
}

interface StreamedChatResponseError {
    options: RawChatOptions;
    parentId: string;
    error: string;
    sessionId?: string;
}

export interface CreateFeedbackRequest {
    messageId: string;
    feedback: boolean;
    comment?: string;
    senderId: string;
}

export interface UpdateFeedbackRequest {
    messageId: string;
    feedbackId: string;
    feedback: boolean;
    comment?: string;
}

export interface CreateFeedbackResponse {
    messageId: string;
    feedback: boolean;
    comment: string;
    senderId: string;
    feedbackId: string;
}
